import * as Sentry from "@sentry/react-native";
import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";

import { SubmitButton } from "@luna-restaurant/forms";
import { useTranslation } from "@luna-restaurant/i18n";
import { Icon, useCustomTheme } from "@luna-restaurant/ui";

import { SecondaryButton } from "@/components/SecondaryButton";
import {
  ClosingTimeForm,
  type ClosingTimeFormValues,
} from "@/components/forms/ClosingTimeForm";
import { ChefHatModal } from "@/components/modals/ChefHatModal";

export type DeleteClosingTimeModalValues = ClosingTimeFormValues;

type DeleteClosingTimeModalProps = {
  visible: boolean;
  closingTime?: Partial<DeleteClosingTimeModalValues> | null;
  schoolClassOptions: { label: string; value: string }[];
  onDismiss: () => void;
  onConfirm: (values: DeleteClosingTimeModalValues) => Promise<void>;
  isSubmitting?: boolean;
};

export function DeleteClosingTimeModal({
  visible,
  closingTime,
  schoolClassOptions,
  onDismiss,
  onConfirm,
  isSubmitting = false,
}: DeleteClosingTimeModalProps) {
  const { colors, fonts } = useCustomTheme();
  const { t } = useTranslation();

  const initialValues = useMemo(() => {
    if (!closingTime) return undefined;
    return {
      editor: closingTime.editor,
      closingNotice: closingTime.closingNotice,
      dateFrom: closingTime.dateFrom,
      dateTo: closingTime.dateTo,
      scopeOrClassId: closingTime.scopeOrClassId,
    } satisfies Partial<DeleteClosingTimeModalValues>;
  }, [closingTime]);

  const handleDismiss = () => {
    if (isSubmitting) return;
    onDismiss();
  };

  const handleConfirm = async (values: DeleteClosingTimeModalValues) => {
    try {
      await onConfirm(values);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  return (
    <ChefHatModal
      visible={visible}
      icon={{ icon: "delete", iconSet: "material-community" }}
      iconSize={50}
      onDismiss={handleDismiss}
    >
      <View style={styles.header}>
        <Icon
          iconSet="font-awesome"
          icon="bell-exclamation"
          style="solid"
          size={20}
          color={colors.danger}
        />
        <Text style={[styles.headerText, fonts.bold, { color: colors.danger }]}>
          {t("closeTimes.deleteModal.header")}
        </Text>
      </View>

      <Text style={[styles.title, fonts.bold, { color: colors.text }]}>
        {t("closeTimes.deleteModal.title")}
      </Text>
      <Text style={[styles.description, fonts.regular, { color: colors.text }]}>
        {t("closeTimes.deleteModal.description")}
      </Text>

      <ClosingTimeForm
        schoolClassOptions={schoolClassOptions}
        initialValues={initialValues}
        fieldLabelMinHeight={42}
        isDateRangeDisabled
        isScopeDisabled
        isClosingNoticeDisabled
        actionsNotice={
          <Text
            style={[
              styles.reviewNoticeText,
              fonts.regular,
              { color: colors.text },
            ]}
          >
            {t("closeTimes.deleteModal.reviewNotice")}
          </Text>
        }
        onSubmit={handleConfirm}
        renderActions={({ submit, reset, isSubmitting: isFormSubmitting }) => {
          const isBusy = isSubmitting || isFormSubmitting;

          return (
            <View style={styles.actions}>
              <SecondaryButton
                label={t("closeTimes.deleteModal.cancelButtonText")}
                onPress={() => {
                  if (isBusy) return;
                  reset();
                  handleDismiss();
                }}
                disabled={isBusy}
                iconName="close"
                iconPosition="left"
              />

              <SubmitButton
                title={
                  isBusy
                    ? t("common.submitting")
                    : t("closeTimes.deleteModal.submitButtonText")
                }
                onPress={submit}
                disabled={isBusy}
                buttonStyle={[
                  styles.primaryButton,
                  { backgroundColor: colors.border },
                ]}
                buttonTextStyle={[
                  styles.primaryButtonText,
                  fonts.bold,
                  { color: colors.background },
                ]}
                iconName="arrow-right"
                iconColor={colors.background}
                iconPosition="right"
                iconSize={24}
              />
            </View>
          );
        }}
      />
    </ChefHatModal>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 24,
  },
  headerText: {
    fontSize: 16,
    lineHeight: 20.8,
  },
  title: {
    fontSize: 32,
    lineHeight: 40,
    marginBottom: 48,
  },
  description: {
    fontSize: 20,
    lineHeight: 25,
    marginBottom: 24,
  },
  actions: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 16,
    marginTop: 10,
  },
  reviewNoticeText: {
    fontSize: 16,
    lineHeight: 20.8,
  },
  primaryButton: {
    borderRadius: 56,
    height: 56,
    paddingHorizontal: 24,
  },
  primaryButtonText: {
    fontSize: 16,
  },
});
